import React from 'react';
import { MuscleGroupStats, getMuscleGroupColor } from '../utils/muscleAnalysis';

interface MuscleGroupChartProps {
  muscleStats: MuscleGroupStats[];
  maxSets?: number;
}

const MuscleGroupChart: React.FC<MuscleGroupChartProps> = ({ 
  muscleStats, 
  maxSets 
}) => {
  // Calculate max sets for scaling if not provided
  const calculatedMaxSets = maxSets || Math.max(...muscleStats.map(stats => stats.totalSets));
  
  // Only show muscles with sets > 0, limit to top 12 for UI
  const displayStats = muscleStats
    .filter(stats => stats.totalSets > 0)
    .slice(0, 12);

  if (displayStats.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <div className="text-2xl mb-2">💪</div>
        <p>No muscle group data available</p>
        <p className="text-sm">Make sure exercises have muscle group metadata</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {displayStats.map((stats) => {
        const primaryPercentage = (stats.primarySets / calculatedMaxSets) * 100;
        const secondaryPercentage = (stats.secondarySets / calculatedMaxSets) * 100;
        const totalPercentage = primaryPercentage + secondaryPercentage;
        const color = getMuscleGroupColor(stats.muscle);
        
        return (
          <div key={stats.muscle} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-gray-900">{stats.muscle}</span>
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span>{stats.totalSets} sets</span>
                <span className="text-gray-400">•</span>
                <span>{stats.exercises.length} exercises</span>
              </div>
            </div>
            
            <div className="relative h-6 bg-gray-100 rounded-full overflow-hidden">
              {/* Primary muscle volume */}
              <div
                className="absolute left-0 top-0 h-full rounded-full transition-all duration-300"
                style={{ 
                  width: `${primaryPercentage}%`,
                  backgroundColor: color,
                  opacity: 0.9
                }}
              />
              
              {/* Secondary muscle volume (lighter overlay) */}
              <div
                className="absolute h-full rounded-full transition-all duration-300"
                style={{ 
                  left: `${primaryPercentage}%`,
                  width: `${secondaryPercentage}%`,
                  backgroundColor: color,
                  opacity: 0.4
                }}
              />
              
              {/* Primary count (if bar is wide enough) */}
              {totalPercentage > 15 && stats.primarySets > 0 && (
                <div
                  className="absolute inset-y-0 flex items-center px-2 text-xs font-medium text-white"
                  style={{ left: '4px' }}
                >
                  {stats.primarySets}
                </div>
              )}
            </div>
            
            {/* Exercise list (show for all muscles) */}
            <div className="text-xs text-gray-500 truncate" title={stats.exercises.join(', ')}>
              {stats.exercises.slice(0, 3).join(', ')}
              {stats.exercises.length > 3 && ` +${stats.exercises.length - 3} more`}
            </div>
          </div>
        );
      })}
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 pt-2 text-xs text-gray-600 border-t">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-blue-600 rounded opacity-90"></div>
          <span>Primary volume</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-blue-600 rounded opacity-40"></div>
          <span>Secondary volume</span>
        </div>
      </div>
    </div>
  );
};

export default MuscleGroupChart;